import {
    getOpenRouterPricePreview,
    type AiModelPrice,
    type OpenRouterPriceSuggestion,
} from '@/lib/api/master/ai-usage-api'
import { useEffect, useState } from 'react'
import { MdArrowForward } from 'react-icons/md'
import { toast } from 'react-toastify'

type SuggestedPrice = NonNullable<OpenRouterPriceSuggestion['suggested']>

const FIELDS: Array<{ field: keyof SuggestedPrice; label: string }> = [
    { field: 'inputUsdPerMTok', label: 'Entrada' },
    { field: 'cachedInputUsdPerMTok', label: 'Cache' },
    { field: 'outputUsdPerMTok', label: 'Saída' },
]

function differs(row: OpenRouterPriceSuggestion): boolean {
    if (!row.suggested) return false
    if (!row.current) return true
    return FIELDS.some(({ field }) => row.current![field] !== row.suggested![field])
}

interface Props {
    /** O formulário como está agora, com o que ainda não foi salvo. */
    formModels: AiModelPrice[]
    onClose: () => void
    /** Leva os preços escolhidos ao formulário; quem grava é o "Salvar" da tabela. */
    onApply: (updates: Array<{ model: string } & SuggestedPrice>) => void
}

/**
 * Prévia dos preços do OpenRouter: antes e depois de cada modelo, já marcando
 * o que mudou. Aplicar só preenche o formulário; a tabela é gravada no Salvar.
 */
export function OpenRouterPriceDialog({ formModels, onClose, onApply }: Props) {
    const [rows, setRows] = useState<OpenRouterPriceSuggestion[] | null>(null)
    const [selected, setSelected] = useState<Set<string>>(new Set())

    // Lido uma vez, ao abrir: o formulário não muda com o diálogo aberto.
    useEffect(() => {
        void (async () => {
            const names = formModels.map(row => row.model.trim()).filter(Boolean)
            const response = await getOpenRouterPricePreview(names)
            if (!response.success || !response.result) {
                toast.error(response.error || 'Não foi possível consultar o OpenRouter')
                onClose()
                return
            }
            // O "antes" é o formulário, não a tabela salva.
            const models = response.result.models.map(row => ({
                ...row,
                current: formModels.find(form => form.model.trim() === row.model) ?? null,
            }))
            setRows(models)
            setSelected(
                new Set(
                    models
                        .filter(row => differs(row) && (row.current || row.inUse))
                        .map(row => row.model)
                )
            )
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const toggle = (model: string) => {
        setSelected(previous => {
            const next = new Set(previous)
            if (next.has(model)) next.delete(model)
            else next.add(model)
            return next
        })
    }

    const handleApply = () => {
        const updates = (rows ?? [])
            .filter(row => row.suggested && selected.has(row.model))
            .map(row => ({ model: row.model, ...row.suggested! }))
        onApply(updates)
    }

    return (
        <dialog className="modal modal-open">
            <div className="modal-box w-11/12 max-w-4xl">
                <h3 className="text-lg font-semibold">Preços do OpenRouter</h3>
                <p className="text-sm text-base-content/60 mt-1">
                    US$ por 1M de tokens. Aparecem os modelos da tabela, os configurados no painel e
                    os usados nos últimos 30 dias. Transcrição não está no OpenRouter: continua
                    manual.
                </p>

                {!rows ? (
                    <div className="flex justify-center py-14">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                ) : (
                    <div className="overflow-x-auto mt-4 max-h-[60vh]">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th className="w-8" />
                                    <th>Modelo</th>
                                    {FIELDS.map(({ field, label }) => (
                                        <th key={field} className="text-right">
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => {
                                    const changed = differs(row)
                                    return (
                                        <tr key={row.model} className={row.suggested ? '' : 'opacity-60'}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    aria-label={`Aplicar preço de ${row.model}`}
                                                    checked={selected.has(row.model)}
                                                    disabled={!changed}
                                                    onChange={() => toggle(row.model)}
                                                />
                                            </td>
                                            <td>
                                                <span className="font-mono text-sm">{row.model}</span>
                                                <div className="flex flex-wrap gap-1 mt-0.5">
                                                    {row.inUse && (
                                                        <span className="badge badge-sm badge-primary badge-outline">
                                                            em uso
                                                        </span>
                                                    )}
                                                    {!row.current && row.suggested && (
                                                        <span className="badge badge-sm badge-ghost">
                                                            fora da tabela
                                                        </span>
                                                    )}
                                                    {!row.suggested && (
                                                        <span className="badge badge-sm badge-ghost">
                                                            não está no OpenRouter
                                                        </span>
                                                    )}
                                                    {row.suggested && !changed && (
                                                        <span className="badge badge-sm badge-ghost">
                                                            sem mudança
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {FIELDS.map(({ field }) => {
                                                const before = row.current?.[field]
                                                const after = row.suggested?.[field]
                                                const fieldChanged =
                                                    after !== undefined && before !== after
                                                return (
                                                    <td key={field} className="text-right tabular-nums whitespace-nowrap">
                                                        {fieldChanged ? (
                                                            <span className="inline-flex items-center gap-1">
                                                                <span className="text-base-content/50 line-through">
                                                                    {before ?? '—'}
                                                                </span>
                                                                <MdArrowForward className="text-base-content/40" />
                                                                <span className="font-medium">{after}</span>
                                                            </span>
                                                        ) : (
                                                            (before ?? after ?? '—')
                                                        )}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!rows || selected.size === 0}
                        onClick={handleApply}
                    >
                        Aplicar {selected.size > 0 ? `${selected.size} ` : ''}no formulário
                    </button>
                </div>
            </div>
            <button type="button" className="modal-backdrop" onClick={onClose} aria-label="Fechar" />
        </dialog>
    )
}
